/**
 * Media Library page - main entry point.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ArrowUpTrayIcon, TagIcon, FolderIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button, PencilIcon, TrashIcon } from '@/components/ui';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useBreakpoint } from '@/hooks';
import {
  useMediaFolders,
  useMediaTags,
  useMediaImages,
  useStorageSummary,
  useBatchDeleteImages,
} from '@/hooks/queries/useMediaQueries';
import type { ImageCategory } from '@/types/media';
import { debug } from '@/utils/debug'; // [MEDIA TIMING] - remove after perf work
import { FolderSidebar } from './components/FolderSidebar';
import { ImageGrid } from './components/ImageGrid';
import { SearchBar } from './components/SearchBar';
import { TagFilter } from './components/TagFilter';
import { CategoryFilter } from './components/CategoryFilter';
import { StorageIndicator } from './components/StorageIndicator';
import { UploadModal } from './components/UploadModal';
import { ImageDetailModal } from './components/ImageDetailModal';
import { FolderModal } from './components/FolderModal';
import { TagManager } from './components/TagManager';
import { BatchEditModal } from './components/BatchEditModal';

export function MediaLibrary() {
  // Responsive
  const { isMobile } = useBreakpoint();
  const [showMobileFolders, setShowMobileFolders] = useState(false);

  // State
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<ImageCategory | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedImageIds, setSelectedImageIds] = useState<Set<string>>(new Set());
  const [viewingImageId, setViewingImageId] = useState<string | null>(null);

  // Modals
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showTagManager, setShowTagManager] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showBatchEditModal, setShowBatchEditModal] = useState(false);

  // Close mobile folder drawer when switching to desktop
  useEffect(() => {
    if (!isMobile) {
      setShowMobileFolders(false);
    }
  }, [isMobile]);

  // Mutations
  const batchDeleteMutation = useBatchDeleteImages();

  // Queries
  const { data: folders = [], isLoading: foldersLoading } = useMediaFolders();
  const { data: tags = [], isLoading: tagsLoading } = useMediaTags();
  const { data: storageSummary, isLoading: storageLoading } = useStorageSummary();

  const { data: imagesResponse, isLoading: imagesLoading } = useMediaImages({
    folder_id: selectedFolderId || undefined,
    tag_ids: selectedTagIds.length > 0 ? selectedTagIds : undefined,
    search: searchQuery || undefined,
    category: selectedCategory,
    page: 1,
    page_size: 50,
    sort_by: 'created_at',
    sort_order: 'desc',
  });

  // [MEDIA TIMING] - remove after perf work
  // Time the list query (filters change -> response arrives) and dump per-thumbnail
  // GCS resource timings once thumbnails have had time to load.
  const listRequestStartRef = useRef<number | null>(null);
  useEffect(() => {
    listRequestStartRef.current = performance.now();
  }, [selectedFolderId, selectedTagIds, selectedCategory, searchQuery]);
  useEffect(() => {
    if (!debug.isDev()) return;
    if (imagesLoading) return;
    if (!imagesResponse) return;
    const start = listRequestStartRef.current;
    if (start == null) return;
    const elapsed = performance.now() - start;
    listRequestStartRef.current = null;
    debug.log('[MEDIA TIMING] list response', {
      items: imagesResponse.images.length,
      total: imagesResponse.total,
      total_ms: Math.round(elapsed),
    });
    // Wait for viewport thumbnails to fetch, then dump resource timings from GCS.
    const handle = window.setTimeout(() => {
      try {
        const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
        const gcs = entries
          .filter((e) => e.startTime >= start && /storage\.googleapis\.com/.test(e.name))
          .map((e) => ({
            name: e.name.split('?')[0].slice(-80),
            duration_ms: Math.round(e.duration),
            ttfb_ms: Math.round(e.responseStart - e.requestStart),
            transfer_bytes: e.transferSize,
            initiator: e.initiatorType,
          }));
        if (gcs.length > 0) {
          console.table(gcs);
          debug.log('[MEDIA TIMING] gcs thumbnails', {
            count: gcs.length,
            total_duration_ms: gcs.reduce((s, r) => s + r.duration_ms, 0),
            avg_ttfb_ms: Math.round(
              gcs.reduce((s, r) => s + r.ttfb_ms, 0) / gcs.length,
            ),
          });
        } else {
          debug.log('[MEDIA TIMING] no GCS resource entries found yet');
        }
      } catch (err) {
        debug.error('[MEDIA TIMING] resource-timing dump failed', err);
      }
    }, 3000);
    return () => window.clearTimeout(handle);
  }, [imagesResponse, imagesLoading]);

  const images = imagesResponse?.images || [];

  // Handlers
  const handleSelectImage = useCallback((id: string, multiSelect: boolean) => {
    setSelectedImageIds((prev) => {
      const next = new Set(prev);
      if (multiSelect) {
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
      } else {
        if (next.has(id) && next.size === 1) {
          next.delete(id);
        } else {
          next.clear();
          next.add(id);
        }
      }
      return next;
    });
  }, []);

  const handleImageClick = useCallback((id: string) => {
    setViewingImageId(id);
  }, []);

  const handleSelectFolder = useCallback((folderId: string | null) => {
    setSelectedFolderId(folderId);
    setSelectedImageIds(new Set());
    setShowMobileFolders(false); // Close mobile drawer when folder selected
  }, []);

  const handleBatchDelete = useCallback(async () => {
    await batchDeleteMutation.mutateAsync(Array.from(selectedImageIds));
    setSelectedImageIds(new Set());
    setShowDeleteConfirm(false);
  }, [selectedImageIds, batchDeleteMutation]);

  const handleSelectAll = useCallback(() => {
    if (selectedImageIds.size === images.length) {
      // Deselect all if all are selected
      setSelectedImageIds(new Set());
    } else {
      // Select all visible images
      setSelectedImageIds(new Set(images.map((img) => img.id)));
    }
  }, [images, selectedImageIds.size]);

  return (
    <div className="flex h-[calc(100vh-48px)]">
      {/* Left Sidebar - Folders (hidden on mobile) */}
      {!isMobile && (
        <div className="w-64 border-r border-gray-200 flex-shrink-0">
          <FolderSidebar
            folders={folders}
            selectedFolderId={selectedFolderId}
            onSelectFolder={handleSelectFolder}
            onCreateFolder={() => setShowFolderModal(true)}
            isLoading={foldersLoading}
          />
        </div>
      )}

      {/* Mobile Folder Drawer */}
      {isMobile && (
        <>
          {/* Overlay */}
          <div
            className={`fixed inset-0 bg-black z-[60] transition-opacity duration-300 ${
              showMobileFolders ? 'opacity-50' : 'opacity-0 pointer-events-none'
            }`}
            onClick={() => setShowMobileFolders(false)}
            aria-hidden="true"
          />
          {/* Drawer */}
          <div
            className={`fixed top-0 left-0 h-full w-72 bg-white z-[70] transform transition-transform duration-300 ease-out shadow-xl ${
              showMobileFolders ? 'translate-x-0' : '-translate-x-full'
            }`}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Folders</h2>
              <button
                onClick={() => setShowMobileFolders(false)}
                className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Close folders"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="h-[calc(100%-60px)] overflow-y-auto">
              <FolderSidebar
                folders={folders}
                selectedFolderId={selectedFolderId}
                onSelectFolder={handleSelectFolder}
                onCreateFolder={() => {
                  setShowMobileFolders(false);
                  setShowFolderModal(true);
                }}
                isLoading={foldersLoading}
              />
            </div>
          </div>
        </>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <div className="p-4 border-b border-gray-200 bg-white">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-3">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">Media Library</h1>
              {/* Mobile: Folders link below heading */}
              {isMobile && (
                <button
                  onClick={() => setShowMobileFolders(true)}
                  className="mt-1 text-sm text-blue-600 flex items-center gap-1"
                >
                  <FolderIcon className="w-4 h-4" />
                  <span>Folders</span>
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 md:gap-4">
              <StorageIndicator summary={storageSummary} isLoading={storageLoading} />
              <div className="ml-auto flex items-center gap-2">
                <Button
                  variant="secondary"
                  onClick={() => setShowTagManager(true)}
                >
                  <TagIcon className="w-4 h-4 md:mr-1" />
                  <span className="hidden md:inline">Tags</span>
                </Button>
                <Button onClick={() => setShowUploadModal(true)}>
                  <ArrowUpTrayIcon className="w-4 h-4 md:mr-1" />
                  <span className="hidden md:inline">Upload</span>
                </Button>
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
            <div className="w-full md:w-64">
              <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search images..."
              />
            </div>
            <CategoryFilter
              selectedCategory={selectedCategory}
              onChange={setSelectedCategory}
            />
            <TagFilter
              tags={tags}
              selectedTagIds={selectedTagIds}
              onChange={setSelectedTagIds}
              isLoading={tagsLoading}
            />
          </div>
        </div>

        {/* Selection bar */}
        {images.length > 0 && (
          <div className="px-4 py-2 md:py-1.5 min-h-[68px] md:min-h-[44px] bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-2">
            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-3">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSelectAll}
                  className="text-sm text-blue-600 hover:underline"
                >
                  {selectedImageIds.size === images.length ? 'Deselect all' : 'Select all'}
                </button>
                {/* Only show Clear when some (but not all) images are selected */}
                {selectedImageIds.size > 0 && selectedImageIds.size !== images.length && (
                  <button
                    onClick={() => setSelectedImageIds(new Set())}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    Clear
                  </button>
                )}
              </div>
              {selectedImageIds.size > 0 && (
                <span className="text-sm text-gray-700">
                  {selectedImageIds.size} image{selectedImageIds.size !== 1 ? 's' : ''} selected
                </span>
              )}
            </div>
            {selectedImageIds.size > 0 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowBatchEditModal(true)}
                >
                  <PencilIcon className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <TrashIcon className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Image Grid */}
        <div className="flex-1 overflow-auto p-4 bg-gray-50">
          <ImageGrid
            images={images}
            selectedIds={selectedImageIds}
            onSelectImage={handleSelectImage}
            onImageClick={handleImageClick}
            isLoading={imagesLoading}
          />

          {/* Pagination info */}
          {imagesResponse && imagesResponse.total > 0 && (
            <div className="mt-4 text-center text-sm text-gray-500">
              Showing {images.length} of {imagesResponse.total} images
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        folders={folders}
        tags={tags}
        currentFolderId={selectedFolderId}
      />

      {viewingImageId && (
        <ImageDetailModal
          imageId={viewingImageId}
          isOpen={!!viewingImageId}
          onClose={() => setViewingImageId(null)}
          folders={folders}
          tags={tags}
        />
      )}

      <FolderModal
        isOpen={showFolderModal}
        onClose={() => setShowFolderModal(false)}
        folders={folders}
        parentFolderId={selectedFolderId}
      />

      <TagManager
        isOpen={showTagManager}
        onClose={() => setShowTagManager(false)}
        tags={tags}
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleBatchDelete}
        title="Delete Images"
        message={`Are you sure you want to delete ${selectedImageIds.size} image${selectedImageIds.size !== 1 ? 's' : ''}? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={batchDeleteMutation.isPending}
      />

      <BatchEditModal
        isOpen={showBatchEditModal}
        onClose={() => setShowBatchEditModal(false)}
        selectedImageIds={Array.from(selectedImageIds)}
        folders={folders}
        tags={tags}
        onSuccess={() => setSelectedImageIds(new Set())}
      />
    </div>
  );
}

export default MediaLibrary;
